import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { loginService, usersService } from '../../../../services';
import { validator } from '../../../../helpers';
import {
    DEFAULT_DOCUMENT_TYPE_ID,
    DEFAULT_DOCUMENT_TYPE_DESCRIPTION,
    DEFAULT_ROLE_ID,
    DEFAULT_ROLE_DESCRIPTION,
    PERSON_ATTRIBUTE_FIRST_NAME,
    PERSON_ATTRIBUTE_LAST_NAME,
    ALERT_SUCCESS_CLASS,
    ALERT_ERROR_CLASS,
    ALERT_SEVERITY_SUCCESS,
    ALERT_SEVERITY_ERROR,
    HTTP_BAD_REQUEST,
    ERROR_KEY_EMAIL_EXISTS,
    ERROR_KEY_USER_EXISTS,
    ONLY_NUMBERS_REGEX,
    ONLY_LETTERS_REGEX,
    MAX_DOCUMENT_LENGTH_BY_TYPE,
    MIN_DOCUMENT_LENGTH_BY_TYPE,
    DEFAULT_MAX_DOCUMENT_LENGTH,
    DEFAULT_MIN_DOCUMENT_LENGTH,
    FORM_FIELDS,
    INITIAL_ERROR_STATE,
    PLATFORM_BACKOFFICE_ID,
    EMAIL_TYPE_BACKOFFICE_ID,
} from '../../../../constants/users/createUser';
import { AddUserSVG, ClientSVG, DNISelectorSVG, EmailSVG } from '../../../../components';
import { Box, Button, CircularProgress, Dialog, FormHelperText, InputAdornment, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { KeyboardArrowDownOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { palette } from '../../../../styles/theme';
import '../../../../styles/sass/clients/createUserDialog.scss';

const dropdownIcon = (props) => (
    <KeyboardArrowDownOutlined sx={{ color: `${palette.secondary[600]} !important` }} {...props} />
);

const capitalizeName = (value) =>
    value.split(' ').map(word => _.capitalize(word)).join(' ');

export function CreateUserDialog ({ show, open, onHide, onClose, setAlert, setOpenAlert }) {
    const { t } = useTranslation();
    const isVisible = show ?? open;
    const handleClose = onHide ?? onClose;

    const [documentType, setDocumentType] = useState(DEFAULT_DOCUMENT_TYPE_ID);
    const [document, setDocument] = useState('');
    const [name, setName] = useState('');
    const [lastname, setLastname] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState(DEFAULT_ROLE_ID);
    const [error, setError] = useState(INITIAL_ERROR_STATE);

    const [documentTypes, setDocumentTypes] = useState([]);
    const [roles, setRoles] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);
    const [hasOptionsError, setHasOptionsError] = useState(false);

    const { maxDocumentLength, minDocumentLength } = useMemo(() => {
        const selectedDoc = documentTypes.find(d => d.id === documentType);
        const description = selectedDoc?.description;
        return {
            maxDocumentLength: (description && MAX_DOCUMENT_LENGTH_BY_TYPE[description]) || DEFAULT_MAX_DOCUMENT_LENGTH,
            minDocumentLength: (description && MIN_DOCUMENT_LENGTH_BY_TYPE[description]) || DEFAULT_MIN_DOCUMENT_LENGTH,
        };
    }, [documentType, documentTypes]);

    useEffect(() => {
        const fetchOptions = async () => {
            setIsLoadingOptions(true);
            setHasOptionsError(false);
            try {
                const [docTypesResponse, rolesResponse] = await Promise.all([
                    usersService.getDocumentTypes(),
                    usersService.getRoles(),
                ]);
                const docTypes = docTypesResponse?.data || docTypesResponse || [];
                const rolesList = rolesResponse?.data || rolesResponse || [];

                const mappedDocs = docTypes.map(doc => ({
                    ...doc,
                    label: t(`users.table.documentTypes.${doc.description}`, doc.description?.toUpperCase() || ''),
                }));
                const mappedRoles = rolesList.map(r => ({
                    ...r,
                    label: t(`users.table.status.${r.description}`, r.description || ''),
                }));

                setDocumentTypes(mappedDocs);
                setRoles(mappedRoles);

                // Set defaults using descriptive constants
                const defaultDoc = mappedDocs.find(
                    d => d.description === DEFAULT_DOCUMENT_TYPE_DESCRIPTION || d.id === DEFAULT_DOCUMENT_TYPE_ID
                );
                if (defaultDoc) setDocumentType(defaultDoc.id);

                const defaultRole = mappedRoles.find(
                    r => r.description === DEFAULT_ROLE_DESCRIPTION || r.id === DEFAULT_ROLE_ID
                );
                if (defaultRole) setRole(defaultRole.id);
            }
            catch (err) {
                setHasOptionsError(true);
            }
            finally {
                setIsLoadingOptions(false);
            }
        };
        fetchOptions();
    }, [t]);

    const createUser = () => {
        if (!validate()) return false;

        setError(INITIAL_ERROR_STATE);
        setIsLoading(true);

        const selectedDocType = documentTypes.find(doc => doc.id === documentType);

        const data = {
            idPlatform: PLATFORM_BACKOFFICE_ID,
            idRol: role,
            person: {
                attributes: [
                    {
                        description: PERSON_ATTRIBUTE_FIRST_NAME,
                        value: capitalizeName(name)
                    }, {
                        description: PERSON_ATTRIBUTE_LAST_NAME,
                        value: lastname.toUpperCase()
                    }
                ],
                emails: [
                    {
                        idType: EMAIL_TYPE_BACKOFFICE_ID,
                        email: email
                    }
                ],
                documents: [
                    {
                        idType: selectedDocType?.id || documentType,
                        document: document
                    }
                ]
            },
            fullname: `${name} ${lastname}`
        };

        loginService
            .register(data)
            .then(() => {
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: ALERT_SEVERITY_SUCCESS,
                    description: t('users.table.menu.add.success'),
                    className: ALERT_SUCCESS_CLASS
                }));
                setIsLoading(false);
                setOpenAlert(true);
                handleClose && handleClose();
            })
            .catch(e => {
                const rawErrorData = e?.response?.data?.error || e?.response?.data?.message || e?.response?.data || '';
                const errorStr = typeof rawErrorData === 'string' ? rawErrorData.toLowerCase() : JSON.stringify(rawErrorData).toLowerCase();
                let description = t('errors.generalDescription');

                if (e?.response?.status === HTTP_BAD_REQUEST) {
                    if (errorStr.includes(ERROR_KEY_EMAIL_EXISTS)) {
                        description = t('errors.emailAlreadyExists');
                    }
                    else if (errorStr.includes(ERROR_KEY_USER_EXISTS)) {
                        description = t('errors.user');
                    }
                }

                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: ALERT_SEVERITY_ERROR,
                    title: t('errors.generalTitle'),
                    description,
                    className: ALERT_ERROR_CLASS
                }));

                setIsLoading(false);
                setOpenAlert(true);
            });
    };

    const onFocus = (key) => {
        if (error[key]) {
            return setError({ ...error, [key]: '' });
        }
    };

    const validate = () => {
        const currentError = { ...error };
        let hasError = false;

        if (!documentType) {
            currentError[FORM_FIELDS.DOCUMENT_TYPE] = t('users.table.menu.add.inputs.documentType.error');
            hasError = true;
        }
        if (!document || !/^\d+$/.test(document) || document.length < minDocumentLength || document.length > maxDocumentLength) {
            const selectedDoc = documentTypes.find(d => d.id === documentType);
            const docTypeName = selectedDoc?.label || t('users.table.columns.document');
            currentError[FORM_FIELDS.DOCUMENT] = t('users.table.menu.add.inputs.document.error', { type: docTypeName });
            hasError = true;
        }
        if (!_.trim(name || '')) {
            currentError[FORM_FIELDS.NAME] = t('users.table.menu.add.inputs.name.error');
            hasError = true;
        }
        if (!_.trim(lastname || '')) {
            currentError[FORM_FIELDS.LAST_NAME] = t('users.table.menu.add.inputs.surname.error');
            hasError = true;
        }
        if (!validator.isEmail(email)) {
            currentError[FORM_FIELDS.EMAIL] = t('users.table.menu.add.inputs.email.error');
            hasError = true;
        }
        if (!role) {
            currentError[FORM_FIELDS.ROLE] = t('users.table.menu.add.inputs.role.error');
            hasError = true;
        }
        if (hasError) {
            setError(currentError);
            return false;
        }
        return true;
    };

    const isFormValid = documentType && document && _.trim(name) && _.trim(lastname) && email && role;

    return (
        <Dialog open={isVisible} onClose={() => handleClose && handleClose()} className='createUserDialog'>
            <Box className='createUserDialog__header'>
                <AddUserSVG />
                <Typography variant='h6' className='createUserDialog__title'>{t('users.table.menu.add.title')}</Typography>
            </Box>

            {isLoadingOptions
                ? (
                    <Box display='flex' justifyContent='center' padding='2rem'>
                        <CircularProgress size='2.462rem' sx={{ color: palette.secondary[600] }} />
                    </Box>
                )
                : hasOptionsError
                    ? (
                        <Box display='flex' justifyContent='center' padding='2rem'>
                            <FormHelperText sx={{ color: palette.error.main, fontSize: '1rem' }}>
                                {t('errors.generalDescription')}
                            </FormHelperText>
                        </Box>
                    )
                    : (
                        <Stack direction='column' gap='1.5rem'>
                            {/* Document type + Document number */}
                            <Stack flexDirection='row' gap='1.846rem'>
                                <Stack direction='column' gap='0.308rem' width='50%'>
                                    <Typography variant='body1' className='createUserDialog__label'>
                                        {t('users.table.menu.add.inputs.documentType.label')}
                                    </Typography>
                                    <Select
                                        value={documentType}
                                        onChange={(e) => { setDocumentType(e.target.value); setDocument(''); onFocus(FORM_FIELDS.DOCUMENT_TYPE); }}
                                        displayEmpty
                                        variant='outlined'
                                        IconComponent={dropdownIcon}
                                        error={!!error.documentType}
                                        className='dialogForm__select'
                                        renderValue={(selected) => {
                                            if (!selected) return <Typography sx={{ color: palette.disabled.main }}>{t('users.table.menu.add.inputs.documentType.label')}</Typography>;
                                            const doc = documentTypes.find(d => d.id === selected);
                                            return (
                                                <Box display='flex' alignItems='center' gap='0.615rem'>
                                                    <DNISelectorSVG customStroke={palette.primary.main} />
                                                    {doc?.label || ''}
                                                </Box>
                                            );
                                        }}
                                    >
                                        {documentTypes.map((doc) => (
                                            <MenuItem key={doc.id} value={doc.id}>
                                                <Box display='flex' alignItems='center' gap='0.615rem'>
                                                    <DNISelectorSVG customStroke={palette.primary.main} />
                                                    {doc.label}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {error.documentType && <FormHelperText sx={{ color: palette.error.main, ml: '0.75rem' }}>{error.documentType}</FormHelperText>}
                                </Stack>

                                <Stack direction='column' gap='0.308rem' width='50%'>
                                    <Typography variant='body1' className='createUserDialog__label'>
                                        {t('users.table.menu.add.inputs.document.label')}
                                    </Typography>
                                    <TextField
                                        className='dialogForm__input'
                                        fullWidth
                                        error={!!error.document}
                                        onFocus={() => onFocus(FORM_FIELDS.DOCUMENT)}
                                        autoComplete='off'
                                        variant='outlined'
                                        placeholder={t('users.table.menu.add.inputs.document.placeholder')}
                                        value={document}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (ONLY_NUMBERS_REGEX.test(val) && val.length <= maxDocumentLength) setDocument(val);
                                        }}
                                        helperText={error.document || ''}
                                        FormHelperTextProps={{ sx: { color: palette.error.main } }}
                                    />
                                </Stack>
                            </Stack>

                            {/* First name + Last name */}
                            <Stack flexDirection='row' gap='1.846rem'>
                                <Stack direction='column' gap='0.308rem' width='50%'>
                                    <Typography variant='body1' className='createUserDialog__label'>
                                        {t('users.table.menu.add.inputs.name.label')}
                                    </Typography>
                                    <TextField
                                        className='dialogForm__input'
                                        fullWidth
                                        error={!!error.name}
                                        onFocus={() => onFocus(FORM_FIELDS.NAME)}
                                        autoComplete='off'
                                        variant='outlined'
                                        placeholder={t('users.table.menu.add.inputs.name.placeholder')}
                                        value={name}
                                        onChange={(e) => {
                                            if (ONLY_LETTERS_REGEX.test(e.target.value)) setName(e.target.value);
                                        }}
                                        helperText={error.name || ''}
                                        FormHelperTextProps={{ sx: { color: palette.error.main } }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <ClientSVG customStroke={palette.primary.main} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Stack>

                                <Stack direction='column' gap='0.308rem' width='50%'>
                                    <Typography variant='body1' className='createUserDialog__label'>
                                        {t('users.table.menu.add.inputs.surname.label')}
                                    </Typography>
                                    <TextField
                                        className='dialogForm__input'
                                        fullWidth
                                        error={!!error.lastName}
                                        onFocus={() => onFocus(FORM_FIELDS.LAST_NAME)}
                                        autoComplete='off'
                                        variant='outlined'
                                        placeholder={t('users.table.menu.add.inputs.surname.placeholder')}
                                        value={lastname}
                                        onChange={(e) => {
                                            if (ONLY_LETTERS_REGEX.test(e.target.value)) setLastname(e.target.value);
                                        }}
                                        helperText={error.lastName || ''}
                                        FormHelperTextProps={{ sx: { color: palette.error.main } }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <ClientSVG customStroke={palette.primary.main} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Stack>
                            </Stack>

                            {/* Email */}
                            <Stack direction='column' gap='0.308rem'>
                                <Typography variant='body1' className='createUserDialog__label'>
                                    {t('users.table.menu.add.inputs.email.label')}
                                </Typography>
                                <TextField
                                    className='dialogForm__input'
                                    fullWidth
                                    error={!!error.email}
                                    onFocus={() => onFocus(FORM_FIELDS.EMAIL)}
                                    autoComplete='off'
                                    variant='outlined'
                                    placeholder={t('users.table.menu.add.inputs.email.placeholder')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.trim())}
                                    helperText={error.email || ''}
                                    FormHelperTextProps={{ sx: { color: palette.error.main } }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <EmailSVG customStroke={palette.primary.main} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Stack>

                            {/* Role */}
                            <Stack direction='column' gap='0.308rem'>
                                <Typography variant='body1' className='createUserDialog__label'>
                                    {t('users.table.menu.add.inputs.role.label')}
                                </Typography>
                                <Select
                                    value={role}
                                    onChange={(e) => { setRole(e.target.value); onFocus(FORM_FIELDS.ROLE); }}
                                    displayEmpty
                                    variant='outlined'
                                    IconComponent={dropdownIcon}
                                    error={!!error.role}
                                    className='dialogForm__select'
                                    renderValue={(selected) => {
                                        if (!selected) return <Typography sx={{ color: palette.disabled.main }}>{t('users.table.menu.add.inputs.role.placeholder')}</Typography>;
                                        const r = roles.find(rl => rl.id === selected);
                                        return r?.label || '';
                                    }}
                                >
                                    {roles.map((r) => (
                                        <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                                    ))}
                                </Select>
                                {error.role && <FormHelperText sx={{ color: palette.error.main, ml: '0.75rem' }}>{error.role}</FormHelperText>}
                            </Stack>
                        </Stack>
                    )
            }

            <Box display='flex' justifyContent='flex-end' marginTop='2rem' gap='2.462rem'>
                <Button className='delete_button--cancel' onClick={handleClose} disabled={isLoading}>
                    {t('buttons.cancel')}
                </Button>
                <Button
                    className='delete_button--submit'
                    onClick={createUser}
                    disabled={isLoading || !isFormValid}
                    sx={{
                        '&.Mui-disabled': {
                            backgroundColor: `${palette.disabled[500]} !important`,
                            color: `${palette.white} !important`,
                        },
                    }}
                >
                    {isLoading ? <CircularProgress size='1.846rem' sx={{ color: palette.white }} /> : t('users.table.menu.add.submit')}
                </Button>
            </Box>
        </Dialog>
    );
};
